/**
 * 欄を離れたときに値を確定する入力欄。
 *
 * 確定のきっかけ: Enter (日本語の変換を確定する Enter は除く) / 欄の外をクリック / 別の欄をクリック。
 * 入力中の文字は欄の中だけで持ち、確定したときに onCommit で親へ渡す。
 * 親が値を整形した場合 (例: 50000 → ¥50,000) や、確定を受け付けなかった場合は親の値に戻る。
 */
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { isImeEnter } from './model';

interface CommitInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange'
> {
  /** 確定済みの値 */
  value: string;
  onCommit: (text: string) => void;
  /** 入力中の文字 (確定前)。品目名の絞り込みに使う */
  onTextChange?: (text: string) => void;
}

export const CommitInput = forwardRef<HTMLInputElement, CommitInputProps>(function CommitInput(
  { value, onCommit, onTextChange, onFocus, onBlur, onKeyDown, ...rest },
  ref,
) {
  const [text, setText] = useState(value);
  const [syncTick, setSyncTick] = useState(0);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setText(value);
  }, [value, syncTick]);

  return (
    <input
      ref={ref}
      autoComplete="off"
      {...rest}
      value={text}
      onFocus={(e) => {
        editingRef.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => {
        setText(e.target.value);
        onTextChange?.(e.target.value);
      }}
      onBlur={(e) => {
        editingRef.current = false;
        onCommit(e.target.value);
        setSyncTick((t) => t + 1);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Enter' && !isImeEnter(e)) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
});

interface CommitTextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'defaultValue' | 'onChange'
> {
  value: string;
  onCommit: (text: string) => void;
}

/** 複数行の欄。Enter で確定、Shift + Enter で改行。 */
export function CommitTextarea({
  value,
  onCommit,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: CommitTextareaProps) {
  const [text, setText] = useState(value);
  const [syncTick, setSyncTick] = useState(0);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setText(value);
  }, [value, syncTick]);

  return (
    <textarea
      {...rest}
      value={text}
      onFocus={(e) => {
        editingRef.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => {
        editingRef.current = false;
        onCommit(e.target.value);
        setSyncTick((t) => t + 1);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Enter' && !e.shiftKey && !isImeEnter(e)) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
}
